/**
 * Maximum number of project/part search results non-admin clients can page through
 * when the search is not scoped to a specific user (e.g. site-wide search and
 * sitemap browse listings). Enforced server-side via Typesense `limit_hits` and by
 * clamping requested pages; searches scoped to a specific owner and admin/internal
 * paths are uncapped.
 */
export const UNSCOPED_SEARCH_RESULT_LIMIT = 10_000;

/**
 * Human-readable form of {@link UNSCOPED_SEARCH_RESULT_LIMIT} for UI copy,
 * e.g. "first 10,000 of many results".
 */
export const UNSCOPED_SEARCH_RESULT_LIMIT_LABEL = UNSCOPED_SEARCH_RESULT_LIMIT.toLocaleString("en-US");

/** Fields that scope a project search to a specific owner; such searches stay uncapped. */
const USER_SCOPE_FILTER_FIELDS = ["owner_uid", "owner_handle", "organization_owner_uid", "userAccessList"];

/** Returns true when a `filter_by` expression scopes results to a specific user or organization. */
export function isUserScopedFilter(filterBy: string | undefined): boolean {
    if (!filterBy) return false;
    return USER_SCOPE_FILTER_FIELDS.some((field) => filterBy.includes(`${field}:`));
}

/** The deepest page browsable under the cap for a given page size. */
export function maxUnscopedSearchPage(perPage: number): number {
    return Math.max(1, Math.floor(UNSCOPED_SEARCH_RESULT_LIMIT / perPage));
}

/** Clamps a requested 1-based page number to the range browsable under the cap. */
export function clampUnscopedSearchPage(page: number, perPage: number): number {
    if (!Number.isFinite(page)) return 1;
    return Math.min(Math.max(1, Math.floor(page)), maxUnscopedSearchPage(perPage));
}

/** Caps a Typesense `found` total to the browsable maximum. */
export function capUnscopedTotalHits(found: number): number {
    return Math.min(found, UNSCOPED_SEARCH_RESULT_LIMIT);
}

/** Returns true when a Typesense `found` total exceeds the browsable maximum. */
export function isUnscopedTotalCapped(found: number): boolean {
    return found > UNSCOPED_SEARCH_RESULT_LIMIT;
}
